import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ResourceDetail,
  type ResourceUpdateInput,
  type UitMailboxRemoveResult,
  type UitMailboxResult,
  ResourceDetail as ResourceDetailSchema,
  UitMailboxRemoveResult as UitMailboxRemoveResultSchema,
  UitMailboxResult as UitMailboxResultSchema,
  isResourceComplete,
} from '@uit/shared/schemas/resource';
import {
  BACKGROUND_CHECK_STATUS_LABELS,
  type BackgroundCheck,
  type BackgroundCheckListResponse,
  type BackgroundCheckOrderResult,
  BackgroundCheckListResponse as BackgroundCheckListResponseSchema,
  BackgroundCheckOrderResult as BackgroundCheckOrderResultSchema,
  isOpenBackgroundCheckStatus,
} from '@uit/shared/schemas/background-check';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { DeleteDocumentButton } from '../components/DeleteDocumentButton.js';
import { apiMutation, useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useFeatureFlag } from '../lib/feature-flags.js';
import { COMPANY_LABELS, DOC_LABELS } from '../lib/labels.js';
import s from './ResourceDetailPage.module.scss';

// Form-local schema: HTML inputs are always strings (never null), so the
// nullable person fields are allowed to be ''. formToUpdate converts '' → null
// before sending, where the strict shared ResourceUpdateInput applies.
const isEmail = (v: string): boolean => z.string().email().safeParse(v).success;

const PersonForm = z.object({
  resource_name: z.string().trim().min(1, 'Required').max(200),
  role: z.string().max(200),
  resource_email: z.string().max(320).refine((v) => v === '' || isEmail(v), 'Enter a valid email address'),
  mobile_phone: z.string().max(50),
  resource_mailing_address_street: z.string().max(400),
  resource_mailing_address_city: z.string().max(200),
  resource_mailing_address_state: z.string().max(100),
  resource_mailing_address_zip: z.string().max(20),
  // Lifecycle (T-156). The date input yields '' or 'YYYY-MM-DD'.
  active: z.boolean(),
  offboarded_date: z
    .string()
    .refine((v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Enter a valid date'),
});
type PersonForm = z.infer<typeof PersonForm>;

function resourceToForm(d: ResourceDetail): PersonForm {
  return {
    resource_name: d.resource_name,
    role: d.role ?? '',
    resource_email: d.resource_email ?? '',
    mobile_phone: d.mobile_phone ?? '',
    resource_mailing_address_street: d.resource_mailing_address_street ?? '',
    resource_mailing_address_city: d.resource_mailing_address_city ?? '',
    resource_mailing_address_state: d.resource_mailing_address_state ?? '',
    resource_mailing_address_zip: d.resource_mailing_address_zip ?? '',
    active: d.active,
    offboarded_date: d.offboarded_date ?? '',
  };
}

function formToUpdate(v: PersonForm): ResourceUpdateInput {
  const nullable = (x: string): string | null => (x.trim() === '' ? null : x.trim());
  return {
    resource_name: v.resource_name.trim(),
    role: nullable(v.role),
    resource_email: nullable(v.resource_email),
    mobile_phone: nullable(v.mobile_phone),
    resource_mailing_address_street: nullable(v.resource_mailing_address_street),
    resource_mailing_address_city: nullable(v.resource_mailing_address_city),
    resource_mailing_address_state: nullable(v.resource_mailing_address_state),
    resource_mailing_address_zip: nullable(v.resource_mailing_address_zip),
    // Server forces inactive when an offboarded date is set (T-156).
    active: v.active,
    offboarded_date: v.offboarded_date.trim() === '' ? null : v.offboarded_date,
  };
}

export function ResourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const o365Mailbox = useFeatureFlag('o365_mailbox');
  const checkrEnabled = useFeatureFlag('checkr');
  const { data, error, loading, refetch } = useApi<ResourceDetail>(
    id ? `/api/resources/${id}` : null,
    ResourceDetailSchema,
  );
  const {
    data: checksData,
    refetch: refetchChecks,
  } = useApi<BackgroundCheckListResponse>(
    id && checkrEnabled ? `/api/resources/${id}/background-checks` : null,
    BackgroundCheckListResponseSchema,
  );

  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mailboxBusy, setMailboxBusy] = useState(false);
  const [mailboxMsg, setMailboxMsg] = useState<string | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeMsg, setRemoveMsg] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);
  const [checkrBusy, setCheckrBusy] = useState(false);
  const [checkrMsg, setCheckrMsg] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonForm>({ resolver: zodResolver(PersonForm) });

  useEffect(() => {
    if (data) reset(resourceToForm(data));
  }, [data, reset]);

  if (loading && !data) return <div className={s.loading}>Loading…</div>;
  if (error || !data) {
    const is404 = error?.message.includes('404');
    return (
      <div className={s.notFound}>
        <Link to="/" className={s.back}>
          ← Back to resources
        </Link>
        <div className={s.notFoundBody}>
          {is404 ? 'Resource not found.' : `Failed to load: ${error?.message ?? 'unknown'}`}
        </div>
      </div>
    );
  }

  const canEdit = admin?.role === 'admin';
  const location = [data.resource_mailing_address_city, data.resource_mailing_address_state]
    .filter(Boolean)
    .join(', ');

  const latestCheck: BackgroundCheck | null = checksData?.items[0] ?? null;
  const checkInProgress = latestCheck
    ? isOpenBackgroundCheckStatus(latestCheck.status)
    : false;
  const checkrMissingEmail = !data.resource_email?.trim();
  const checkrMissingState = !data.resource_mailing_address_state?.trim();
  const checkrPreflightBlocked = checkrMissingEmail || checkrMissingState;
  const checkrOrderBlockedReason = checkrPreflightBlocked
    ? checkrMissingEmail && checkrMissingState
      ? 'Personal email and mailing state are required before ordering a background check.'
      : checkrMissingEmail
        ? 'Personal email is required before ordering a background check.'
        : 'Mailing state is required before ordering a background check.'
    : checkInProgress
      ? 'A background check is already in progress for this resource.'
      : undefined;

  const orderBackgroundCheck = async (): Promise<void> => {
    if (!id) return;
    setCheckrBusy(true);
    setCheckrMsg(null);
    try {
      const result = await apiMutation<Record<string, never>, BackgroundCheckOrderResult>(
        `/api/resources/${id}/background-checks`,
        {},
        { method: 'POST', schema: BackgroundCheckOrderResultSchema },
      );
      if (result.status === 'ordered') {
        await refetchChecks();
      } else {
        setCheckrMsg(result.message ?? 'Could not order the background check.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('422')) {
        setCheckrMsg('Email and mailing state are required before ordering a background check.');
      } else if (message.includes('409')) {
        setCheckrMsg('A background check is already in progress for this resource.');
      } else {
        setCheckrMsg('Could not order the background check. Please try again.');
      }
    } finally {
      setCheckrBusy(false);
    }
  };

  const startEdit = (): void => {
    reset(resourceToForm(data));
    setSubmitError(null);
    setEditing(true);
  };
  const cancelEdit = (): void => {
    setEditing(false);
    setSubmitError(null);
  };
  // Create (or retry) the resource's uIT O365 mailbox (T-114). Auto-runs on
  // acceptance; this is the manual path for the no-seats / failure case.
  const createMailbox = async (): Promise<void> => {
    if (!id) return;
    setMailboxBusy(true);
    setMailboxMsg(null);
    try {
      const result = await apiMutation<Record<string, never>, UitMailboxResult>(
        `/api/resources/${id}/uit-mailbox`,
        {},
        { method: 'POST', schema: UitMailboxResultSchema },
      );
      if (result.status === 'created' || result.status === 'already_exists') {
        await refetch();
      } else {
        setMailboxMsg(result.message ?? 'Could not create the mailbox.');
      }
    } catch {
      setMailboxMsg('Could not create the mailbox. Please try again.');
    } finally {
      setMailboxBusy(false);
    }
  };

  // Manually remove the resource's uIT mailbox (T-141) — deletes the Entra user
  // + frees the seat. Symmetric to createMailbox; confirm-gated in the UI.
  const removeMailbox = async (): Promise<void> => {
    if (!id) return;
    setRemoveBusy(true);
    setRemoveMsg(null);
    try {
      const result = await apiMutation<Record<string, never>, UitMailboxRemoveResult>(
        `/api/resources/${id}/uit-mailbox`,
        {},
        { method: 'DELETE', schema: UitMailboxRemoveResultSchema },
      );
      if (result.status === 'removed' || result.status === 'none') {
        setRemoveConfirm(false);
        await refetch();
      } else {
        setRemoveMsg(result.message ?? 'Could not remove the mailbox.');
      }
    } catch {
      setRemoveMsg('Could not remove the mailbox. Please try again.');
    } finally {
      setRemoveBusy(false);
    }
  };

  // Hard-delete a garbage row (T-156). Returns 204 (no body), so this uses raw
  // fetch rather than apiMutation. Blocked server-side (409) when the resource
  // has billing history — surface that as guidance to deactivate instead.
  const hardDelete = async (): Promise<void> => {
    if (!id) return;
    setDeleteBusy(true);
    setDeleteMsg(null);
    try {
      const res = await fetch(`/api/resources/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.status === 409) {
        setDeleteMsg(
          'This resource has billing history and can’t be deleted. Mark it inactive instead.',
        );
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      navigate('/');
    } catch {
      setDeleteMsg('Could not delete this resource. Please try again.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const onSubmit = async (values: PersonForm): Promise<void> => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiMutation<ResourceUpdateInput, ResourceDetail>(
        `/api/resources/${id}`,
        formToUpdate(values),
        { method: 'PATCH', schema: ResourceDetailSchema },
      );
      await refetch();
      setEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('404')) setSubmitError('This resource no longer exists.');
      else if (message.includes('resource_name_already_exists'))
        setSubmitError(
          'Another resource already has this name. If this is the same person under a new vendor, reassign the vendor on their engagement instead.',
        );
      else if (message.includes('400')) setSubmitError('Some fields are invalid. Check and try again.');
      else setSubmitError('Could not save changes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <Link to="/" className={s.back}>
        ← Back to resources
      </Link>

      <div className={s.header}>
        <h1 className={s.title}>
          {data.resource_name}
          {!data.active && <span className={s.inactiveBadge}>Inactive</span>}
          {!isResourceComplete(data) && <span className={s.incomplete}>Incomplete</span>}
        </h1>
        <p className={s.subtitle}>
          {data.role ?? 'Role unknown'}
          {data.worker_id && ` · Worker ID ${data.worker_id}`}
        </p>
      </div>

      <section className={s.section}>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitleFlush}>Person</h2>
          {canEdit && !editing && (
            <button type="button" className={s.editBtn} onClick={startEdit}>
              Edit
            </button>
          )}
        </div>

        {!editing && (
          <>
            <dl className={s.grid}>
              <Field label="Full name" value={data.resource_name} />
              <Field label="Role" value={data.role} />
              <Field label="Email" value={data.resource_email} />
              <Field label="uIT email" value={data.uit_email} />
              <Field label="Mobile phone" value={data.mobile_phone} />
              <Field label="Worker ID" value={data.worker_id} />
              <Field label="Status" value={data.active ? 'Active' : 'Inactive'} />
              <Field label="Offboarded" value={data.offboarded_date} />
            </dl>
            {o365Mailbox && canEdit && !data.uit_email && (
              <div className={s.mailboxRow} style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className={s.editBtn}
                  onClick={() => void createMailbox()}
                  disabled={mailboxBusy}
                >
                  {mailboxBusy ? 'Creating…' : 'Create uIT mailbox'}
                </button>
                <span className={s.roHint}>
                  Creates {'FirstnameLastname@ultimumit.com'} via Microsoft 365 (auto-created when
                  the onboarding is created).
                </span>
                {mailboxMsg && (
                  <div className={s.formError} role="alert" style={{ marginTop: 8 }}>
                    {mailboxMsg}
                  </div>
                )}
              </div>
            )}
            {o365Mailbox && canEdit && data.uit_email && (
              <div className={s.mailboxRow} style={{ marginTop: 16 }}>
                {!removeConfirm ? (
                  <>
                    <button
                      type="button"
                      className={s.editBtn}
                      onClick={() => setRemoveConfirm(true)}
                      disabled={removeBusy}
                    >
                      Remove uIT mailbox
                    </button>
                    <span className={s.roHint}>
                      Deletes the Entra user + frees the M365 seat — use for an abandoned onboarding.
                    </span>
                  </>
                ) : (
                  <>
                    <span className={s.roHint}>Permanently delete {data.uit_email}?</span>
                    <button
                      type="button"
                      className={s.editBtn}
                      style={{ color: '#dc2626', borderColor: '#dc2626' }}
                      onClick={() => void removeMailbox()}
                      disabled={removeBusy}
                    >
                      {removeBusy ? 'Removing…' : 'Yes, remove'}
                    </button>
                    <button
                      type="button"
                      className={s.editBtn}
                      onClick={() => setRemoveConfirm(false)}
                      disabled={removeBusy}
                    >
                      Cancel
                    </button>
                  </>
                )}
                {removeMsg && (
                  <div className={s.formError} role="alert" style={{ marginTop: 8 }}>
                    {removeMsg}
                  </div>
                )}
              </div>
            )}
            {checkrEnabled && (
              <div className={s.mailboxRow} style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                  {latestCheck && (
                    <>
                      <p className={s.roHint} style={{ margin: 0 }}>
                        Background check:{' '}
                        <strong>{BACKGROUND_CHECK_STATUS_LABELS[latestCheck.status]}</strong>
                        {latestCheck.report_dashboard_url && (
                          <>
                            {' · '}
                            <a
                              href={latestCheck.report_dashboard_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open in Checkr
                            </a>
                          </>
                        )}
                      </p>
                      {(latestCheck.status === 'needs_review' ||
                        latestCheck.status === 'pre_adverse_action' ||
                        latestCheck.status === 'disputed') && (
                        <p className={s.roHint} style={{ margin: 0 }}>
                          Adjudication continues in Checkr
                          {latestCheck.report_dashboard_url
                            ? ' (use Open in Checkr).'
                            : '.'}{' '}
                          The portal only reflects status updates.
                        </p>
                      )}
                    </>
                  )}
                  {canEdit && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* title on the wrapper: disabled buttons don't receive hover in most browsers */}
                        <span title={checkrOrderBlockedReason}>
                          <button
                            type="button"
                            className={s.editBtn}
                            onClick={() => void orderBackgroundCheck()}
                            disabled={checkrBusy || checkInProgress || checkrPreflightBlocked}
                          >
                            {checkrBusy
                              ? 'Ordering…'
                              : checkInProgress
                                ? 'Check in progress'
                                : 'Order background check'}
                          </button>
                        </span>
                        <span className={s.roHint}>
                          Sends a Checkr invitation to the resource&apos;s personal email (US only).
                        </span>
                      </div>
                      {checkrMsg && (
                        <div className={s.formError} role="alert">
                          {checkrMsg}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {(data.resource_mailing_address_street || location) && (
              <p className={s.addr} style={{ marginTop: 16 }}>
                {data.resource_mailing_address_street}
                {data.resource_mailing_address_street && <br />}
                {location}
                {data.resource_mailing_address_zip ? ` ${data.resource_mailing_address_zip}` : ''}
              </p>
            )}
          </>
        )}

        {editing && (
          <form
            onSubmit={(e) => {
              void handleSubmit(onSubmit)(e);
            }}
            noValidate
          >
            {submitError && (
              <div className={s.formError} role="alert">
                {submitError}
              </div>
            )}
            <div className={s.editGrid}>
              <EditField id="resource_name" label="Full name" register={register} error={errors.resource_name?.message} />
              <EditField id="role" label="Role" register={register} error={errors.role?.message} />
              <EditField id="resource_email" label="Email" type="email" register={register} error={errors.resource_email?.message} />
              <EditField id="mobile_phone" label="Mobile phone" type="tel" register={register} error={errors.mobile_phone?.message} />
              <div className={s.full}>
                <span className={s.label}>Mailing address</span>
                <div className={s.addrRow}>
                  <input className={s.input} placeholder="Street" {...register('resource_mailing_address_street')} />
                  <input className={s.input} placeholder="City" {...register('resource_mailing_address_city')} />
                  <input className={s.input} placeholder="State" maxLength={2} {...register('resource_mailing_address_state')} />
                  <input className={s.input} placeholder="ZIP" {...register('resource_mailing_address_zip')} />
                </div>
              </div>
              <div>
                <span className={s.label}>Worker ID</span>
                <input className={s.input} value={data.worker_id ?? ''} disabled />
                <div className={s.roHint}>Assigned by the Cigna VMS during billing — not editable here.</div>
              </div>
              <div className={s.field}>
                <label className={s.label} htmlFor="offboarded_date">
                  Offboarded date
                </label>
                <input
                  id="offboarded_date"
                  type="date"
                  className={s.input}
                  {...register('offboarded_date')}
                />
                {errors.offboarded_date && (
                  <div className={s.fieldError}>{errors.offboarded_date.message}</div>
                )}
              </div>
              <div className={s.field}>
                <span className={s.label}>Status</span>
                <label className={s.checkboxRow}>
                  <input type="checkbox" {...register('active')} />
                  Active (in the resources list)
                </label>
                <div className={s.roHint}>
                  Uncheck to hide from the default list. Setting an offboarded date also marks
                  inactive.
                </div>
              </div>
            </div>
            <div className={s.actions}>
              <button type="button" className={s.cancel} onClick={cancelEdit} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className={s.save} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Relationships</h2>
        <p className={s.sectionNote}>
          The person belongs to a vendor (their business) and has one or more engagements (work
          arrangements). Each opens its own page.
        </p>
        <div className={s.rel}>
          <span className={s.relTag}>Vendor</span>
          <div className={s.relMain}>
            <div className={s.relName}>{data.business_name}</div>
            <div className={s.relSub}>
              {data.company_type ? (COMPANY_LABELS[data.company_type] ?? data.company_type) : '—'}
            </div>
          </div>
          <Link className={s.relGo} to={`/vendors/${data.vendor_id}`}>
            View vendor →
          </Link>
        </div>
        {data.engagements.map((e) => (
          <div key={e.id} className={s.rel}>
            <span className={s.relTag}>Engagement</span>
            <div className={s.relMain}>
              <div className={s.relName}>{e.product_service}</div>
              <div className={s.relSub}>
                {e.client_name} · <span className={s.badge}>{e.status}</span>
              </div>
            </div>
            <Link className={s.relGo} to={`/engagements/${e.id}`}>
              View engagement →
            </Link>
          </div>
        ))}
        {data.engagements.length === 0 && <p className={s.muted}>No engagements.</p>}
      </section>

      {data.documents.length > 0 && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Resource documents</h2>
          <ul className={s.docList}>
            {data.documents.map((d) => (
              <li key={d.id} className={s.docRow}>
                <span className={s.docKind}>{DOC_LABELS[d.kind] ?? d.kind}</span>
                <a className={s.docLink} href={d.download_url} target="_blank" rel="noreferrer">
                  {d.original_filename}
                </a>
                <DeleteDocumentButton
                  id={d.id}
                  filename={d.original_filename}
                  onDeleted={() => void refetch()}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {canEdit && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Danger zone</h2>
          <p className={s.sectionNote}>
            Permanently delete this resource and its engagements. Use only for a genuine junk row —
            for a real person who rolled off, mark them inactive instead. Blocked if the resource
            has billing history.
          </p>
          {!deleteConfirm ? (
            <button
              type="button"
              className={s.editBtn}
              style={{ color: '#dc2626', borderColor: '#dc2626' }}
              onClick={() => {
                setDeleteMsg(null);
                setDeleteConfirm(true);
              }}
            >
              Delete resource
            </button>
          ) : (
            <div className={s.mailboxRow}>
              <span className={s.roHint}>Permanently delete {data.resource_name}?</span>
              <button
                type="button"
                className={s.editBtn}
                style={{ color: '#dc2626', borderColor: '#dc2626' }}
                onClick={() => void hardDelete()}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                type="button"
                className={s.editBtn}
                onClick={() => setDeleteConfirm(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
            </div>
          )}
          {deleteMsg && (
            <div className={s.formError} role="alert" style={{ marginTop: 8 }}>
              {deleteMsg}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? <span className={s.muted}>—</span>}</dd>
    </div>
  );
}

function EditField({
  id,
  label,
  type = 'text',
  register,
  error,
}: {
  id: keyof PersonForm;
  label: string;
  type?: string;
  register: ReturnType<typeof useForm<PersonForm>>['register'];
  error?: string | undefined;
}) {
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={id}>
        {label}
      </label>
      <input id={id} type={type} className={s.input} aria-invalid={!!error} {...register(id)} />
      {error && <div className={s.fieldError}>{error}</div>}
    </div>
  );
}
